import { useQuery } from '@tanstack/react-query';
import { getHealthDetail, getCostSummary, HealthDetail, CostSummary } from '../lib/api';
import { DollarSign, Database, Activity, AlertCircle, Cpu } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Dashboard() {
  const { data: health, isLoading: healthLoading, error: healthError } = useQuery<HealthDetail>({
    queryKey: ['health'],
    queryFn: getHealthDetail,
  });

  const { data: costs, isLoading: costsLoading, error: costsError } = useQuery<CostSummary>({
    queryKey: ['cost-summary'],
    queryFn: getCostSummary,
  });

  if (healthError || costsError) {
    return (
      <div className="flex items-center justify-center h-64 text-red-400">
        <AlertCircle className="h-5 w-5 mr-2" />
        Error loading dashboard data. Check your API key in Settings.
      </div>
    );
  }

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Dashboard Overview</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Businesses"
          value={healthLoading ? '...' : String(health?.total_businesses ?? 0)}
          icon={<Database className="h-5 w-5 text-blue-400" />}
        />
        <StatCard 
          title="MTD Cost"
          value={costsLoading ? '...' : formatCurrency(costs?.mtd_total_cost_usd ?? 0)}
          icon={<DollarSign className="h-5 w-5 text-emerald-400" />}
        />
        <StatCard 
          title="Projected Month-End"
          value={costsLoading ? '...' : formatCurrency(costs?.projected_month_end_usd ?? 0)}
          icon={<Activity className="h-5 w-5 text-amber-400" />}
        />
        <StatCard 
          title="Redis Status"
          value={healthLoading ? '...' : (health?.redis_connected ? 'Connected' : 'Disconnected')}
          icon={<Cpu className={cn("h-5 w-5", health?.redis_connected ? "text-green-400" : "text-red-400")} />}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jobs by Status */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">Jobs by Status</h2>
          {healthLoading ? (
            <div className="text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(health?.jobs_by_status ?? {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="capitalize text-slate-300">{status.replace('_', ' ')}</span>
                  <span className="font-mono font-medium text-emerald-400">{count}</span>
                </div>
              ))}
              {Object.keys(health?.jobs_by_status ?? {}).length === 0 && (
                <div className="text-slate-500 text-sm">No jobs recorded yet.</div>
              )}
            </div>
          )}
        </div>

        {/* Budget Utilization */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">Budget Utilization (Today)</h2>
          {healthLoading ? (
            <div className="text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(health?.budget_utilization ?? {}).map(([provider, data]) => (
                <div key={provider}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize text-slate-300">{provider}</span>
                    <span className="text-slate-400">
                      {formatCurrency(data.spent_usd)} / {formatCurrency(data.cap_usd)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2">
                    <div 
                      className={cn(
                        "h-2 rounded-full transition-all",
                        data.utilization_pct > 90 ? "bg-red-500" :
                        data.utilization_pct > 70 ? "bg-amber-500" : "bg-emerald-500"
                      )}
                      style={{ width: `${Math.min(data.utilization_pct, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
              {Object.keys(health?.budget_utilization ?? {}).length === 0 && (
                <div className="text-slate-500 text-sm">No budgets configured.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cost by Block */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 text-slate-100">Cost by Analysis Block (MTD)</h2>
        {costsLoading ? (
          <div className="text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 rounded-l-lg">Block</th>
                  <th className="px-4 py-3">Total Calls</th>
                  <th className="px-4 py-3 rounded-r-lg text-right">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {costs?.by_block.map((row) => (
                  <tr key={row.block} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="px-4 py-3 font-mono text-slate-300">{row.block}</td>
                    <td className="px-4 py-3 text-slate-400">{row.calls.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-400">
                      {formatCurrency(row.cost_usd)}
                    </td>
                  </tr>
                ))}
                {(!costs?.by_block || costs.by_block.length === 0) && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                      No cost data available for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon }: { title: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-100">{value}</p>
      </div>
      <div className="p-2 bg-slate-800 rounded-lg">
        {icon}
      </div>
    </div>
  );
}
